import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from '../api/client';
import { API } from '../api/config';

interface User {
  id: string;
  email: string;
  full_name?: string;
  avatar_url?: string;
  is_admin: boolean;
  is_active: boolean;
  is_verified: boolean;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (provider: 'google' | 'github') => Promise<void>;
  loginWithEmail: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, fullName?: string) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (token: string, password: string) => Promise<void>;
  verifyEmail: (token: string) => Promise<void>;
  resendVerification: (email: string) => Promise<void>;
  logout: () => void;
  getCurrentUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('access_token');
      if (token) {
        try {
          const response = await axios.get(API.getCurrentUser(), {
            headers: { Authorization: `Bearer ${token}` },
          });
          setUser(response.data);
        } catch (error) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
        }
      }
      setLoading(false);
    };
    
    checkAuth();
  }, []);
  
  const login = async (provider: 'google' | 'github') => {
    try {
      const loginUrl = provider === 'google' ? API.loginGoogle() : API.loginGithub();
      const response = await axios.get(loginUrl);
      window.location.href = response.data.authorization_url;
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };
  
  const loginWithEmail = async (email: string, password: string) => {
    try {
      const response = await axios.post(API.login(), {
        email,
        password,
      });
      
      localStorage.setItem('access_token', response.data.access_token);
      localStorage.setItem('refresh_token', response.data.refresh_token);
      setUser(response.data.user);
    } catch (error) {
      console.error('Email login failed:', error);
      throw error;
    }
  };
  
  const register = async (email: string, password: string, fullName?: string) => {
    try {
      const response = await axios.post(API.register(), {
        email,
        password,
        full_name: fullName,
      });
      
      localStorage.setItem('access_token', response.data.access_token);
      localStorage.setItem('refresh_token', response.data.refresh_token);
      setUser(response.data.user);
    } catch (error) {
      console.error('Registration failed:', error);
      throw error;
    }
  };

  const forgotPassword = async (email: string) => {
    try {
      await axios.post(API.forgotPassword(), { email });
    } catch (error) {
      console.error('Forgot password request failed:', error);
      throw error;
    }
  };

  const resetPassword = async (token: string, password: string) => {
    try {
      await axios.post(API.resetPassword(), {
        token,
        new_password: password,
      });
    } catch (error) {
      console.error('Reset password failed:', error);
      throw error;
    }
  };
  
  const verifyEmail = async (token: string) => {
    try {
      await axios.post(API.verifyEmail(), { token });
      await getCurrentUser();
    } catch (error) {
      console.error('Email verification failed:', error);
      throw error;
    }
  };

  const resendVerification = async (email: string) => {
    try {
      await axios.post(API.resendVerification(), { email });
    } catch (error) {
      console.error('Resend verification failed:', error);
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setUser(null);
    window.location.href = '/login';
  };
  
  const getCurrentUser = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (token) {
        const response = await axios.get(API.getCurrentUser(), {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(response.data);
      }
    } catch (error) {
      console.error('Failed to get current user:', error);
    }
  };
  
  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAuthenticated: !!user,
        login,
        loginWithEmail,
        register,
        forgotPassword,
        resetPassword,
        verifyEmail,
        resendVerification,
        logout,
        getCurrentUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
